/**
 * Utility functions for placeholder replacement with index support
 */

import { escapeForJSON } from './placeholderUtils';

/**
 * Replaces @CUAPROMPT@ placeholders in form data with escaped user prompt
 */
export const replacePlaceholders = (
  formData: Record<string, unknown>,
  userPrompt: string | null | undefined
): Record<string, unknown> => {
  const processedData = JSON.parse(JSON.stringify(formData)) as Record<
    string,
    unknown
  >;

  const replaceInObject = (obj: unknown): unknown => {
    if (typeof obj === 'string') {
      if (userPrompt != null) {
        // Escape special characters in user prompt to prevent JSON breakage
        const escapedPrompt = escapeForJSON(userPrompt);

        return obj.replace(/@CUAPROMPT@/g, escapedPrompt);
      }

      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map((item: unknown) => replaceInObject(item));
    }

    if (obj && typeof obj === 'object') {
      const result: Record<string, unknown> = {};

      Object.entries(obj as Record<string, unknown>).forEach(([key, value]) => {
        result[key] = replaceInObject(value);
      });

      return result;
    }

    return obj;
  };

  return replaceInObject(processedData) as Record<string, unknown>;
};

/**
 * Enhanced placeholder replacement for AddAccess and RemoveAccess with index support
 * Handles accumulated prompts for all entitlements up to the current index
 */
export const replacePlaceholdersWithIndex = (
  formData: Record<string, unknown>,
  userPrompt: string | null | undefined,
  entitlementIndex: number,
  allPrompts?: (string | null)[]
): Record<string, unknown> => {
  const processedData = JSON.parse(JSON.stringify(formData)) as Record<
    string,
    unknown
  >;

  const replaceInString = (str: string): string => {
    let result = str;

    // Replace index-related placeholders everywhere
    result = result.replace(/@ENTITLEMENTINDEX@/g, entitlementIndex.toString());
    result = result.replace(/@CURRENTINDEX@/g, entitlementIndex.toString());

    // Handle @CUAPROMPT@ replacement for JSON strings containing call arrays
    if (result.includes('@CUAPROMPT@')) {
      // Check if this string contains JSON with a "call" array
      if (result.includes('"call":[')) {
        try {
          const parsedJson = JSON.parse(result) as { call?: unknown[] };

          if (parsedJson.call && Array.isArray(parsedJson.call)) {
            // Replace @CUAPROMPT@ for all entitlements using allPrompts array
            const callArray = parsedJson.call;

            callArray.forEach((item: unknown, index: number) => {
              let promptToUse: string | null = null;

              if (allPrompts?.[index]?.trim() !== '') {
                // Use stored prompt for this index
                promptToUse = allPrompts?.[index] ?? null;
              } else if (
                index === entitlementIndex &&
                userPrompt &&
                userPrompt.trim() !== ''
              ) {
                // Use current prompt for current index
                promptToUse = userPrompt;
              }

              const itemStr = JSON.stringify(item);
              let updatedItemStr: string;

              if (promptToUse) {
                // Replace with actual prompt
                const escapedPrompt = escapeForJSON(promptToUse);

                updatedItemStr = itemStr.replace(/@CUAPROMPT@/g, escapedPrompt);
              } else {
                // Keep placeholder as-is when no prompt exists
                updatedItemStr = itemStr;
              }

              callArray[index] = JSON.parse(updatedItemStr) as unknown;
            });
            result = JSON.stringify(parsedJson);
          }
        } catch (e) {
          // If parsing fails and we have a valid prompt, replace normally
          if (userPrompt && userPrompt.trim() !== '') {
            const fallbackPrompt = escapeForJSON(userPrompt);

            result = result.replace(/@CUAPROMPT@/g, fallbackPrompt);
          }
        }
      } else if (userPrompt && userPrompt.trim() !== '') {
        // For non-JSON strings, only replace if we have a valid prompt
        const fallbackPrompt = escapeForJSON(userPrompt);

        result = result.replace(/@CUAPROMPT@/g, fallbackPrompt);
      }
    }

    return result;
  };

  const replaceInObject = (obj: unknown): unknown => {
    if (typeof obj === 'string') {
      return replaceInString(obj);
    }

    if (Array.isArray(obj)) {
      return obj.map((item: unknown) => replaceInObject(item));
    }

    if (obj && typeof obj === 'object') {
      const result: Record<string, unknown> = {};

      Object.entries(obj as Record<string, unknown>).forEach(([key, value]) => {
        result[key] = replaceInObject(value);
      });

      return result;
    }

    return obj;
  };

  return replaceInObject(processedData) as Record<string, unknown>;
};
